/**
 * Modale de sélection initiale d'un preset de garantie
 * Affichée au démarrage du wizard pour les utilisateurs INTERNAL
 * Étape 1 : Sélection du type de véhicule
 * Étape 2 : Sélection du preset ou skip
 */

import { useState, useEffect } from 'react';
import { Car, Sparkles, Package, Star, CheckCircle } from 'lucide-react';
import { useWarrantyPresets } from '@/features/warranties/hooks/useWarrantyPresets';
import { useAllowedVehicleTypes } from '@/features/warranties/hooks/useAllowedVehicleTypes';
import { VEHICLE_TYPE_LABELS } from '@/features/warranties/types/warranty.types';
import type { WarrantyPreset } from '@/features/warranties/types/warranty-preset.types';

interface SelectWarrantyPresetModalProps {
  onSelectPreset: (preset: WarrantyPreset) => void;
  onSkip: () => void;
  societyId?: string;
}

export const SelectWarrantyPresetModal = ({
  onSelectPreset,
  onSkip,
  societyId,
}: SelectWarrantyPresetModalProps) => {
  const { allowedVehicleTypes, autoVehicleType, isSingleType } = useAllowedVehicleTypes(societyId);
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | undefined>(undefined);

  // Si un seul type de véhicule autorisé, skip l'étape 1
  useEffect(() => {
    if (isSingleType && autoVehicleType !== undefined) {
      setSelectedVehicleType(autoVehicleType);
      setStep(2);
    }
  }, [isSingleType, autoVehicleType]);

  // Charger les presets uniquement après sélection du vehicleType
  const { data: presets, isLoading } = useWarrantyPresets(selectedVehicleType);

  // Icônes pour les types de véhicules
  const vehicleTypeIcons = [
    { Icon: Car, color: 'tw-text-affinicar-primary', bgColor: 'tw-bg-affinicar-lighter' },
    { Icon: Star, color: 'tw-text-purple-600', bgColor: 'tw-bg-purple-100' },
    { Icon: Package, color: 'tw-text-green-600', bgColor: 'tw-bg-green-100' },
  ];

  // Icônes pour les presets
  const presetIcons = [
    { Icon: Package, color: 'tw-text-green-600', bgColor: 'tw-bg-green-100' },
    { Icon: Star, color: 'tw-text-affinicar-primary', bgColor: 'tw-bg-affinicar-lighter' },
    { Icon: CheckCircle, color: 'tw-text-purple-600', bgColor: 'tw-bg-purple-100' },
  ];

  const handleVehicleTypeSelect = (vehicleType: number) => {
    setSelectedVehicleType(vehicleType);
    setStep(2);
  };

  const handlePresetSelect = (preset: WarrantyPreset) => {
    onSelectPreset(preset);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedVehicleType(undefined);
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-4xl tw-max-h-[90vh] tw-flex tw-flex-col">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b">
          <div className="tw-flex tw-items-center tw-gap-3">
            <Sparkles className="tw-w-6 tw-h-6 tw-text-affinicar-primary" />
            <div>
              <h2 className="tw-text-2xl tw-font-bold">
                {step === 1 ? 'Sélectionnez le type de véhicule' : 'Choisissez un preset'}
              </h2>
              <p className="tw-text-sm tw-text-gray-600">
                {step === 1
                  ? 'Commencez par choisir le type de véhicule pour afficher les presets adaptés'
                  : 'Gagnez du temps avec une configuration prédéfinie'}
              </p>
            </div>
          </div>
        </div>

        {/* Contenu */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-p-6">
          {step === 1 ? (
            // Étape 1 : Sélection du type de véhicule
            <div className={`tw-grid tw-grid-cols-1 md:tw-grid-cols-${allowedVehicleTypes.length} tw-gap-4`}>
              {Object.entries(VEHICLE_TYPE_LABELS)
                .filter(([type]) => allowedVehicleTypes.includes(parseInt(type)))
                .map(([type, label], index) => {
                const { Icon, color, bgColor } = vehicleTypeIcons[index] || vehicleTypeIcons[0];
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleVehicleTypeSelect(parseInt(type))}
                    className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8 tw-rounded-xl tw-border-2 tw-border-gray-300 tw-bg-white hover:tw-border-affinicar-light hover:tw-shadow-lg tw-transition-all tw-text-center"
                  >
                    <div className={`tw-p-4 tw-rounded-full tw-mb-4 ${bgColor}`}>
                      <Icon className={`tw-w-8 tw-h-8 ${color}`} />
                    </div>
                    <h3 className="tw-text-lg tw-font-bold tw-text-gray-900">{label}</h3>
                  </button>
                );
              })}
            </div>
          ) : (
            // Étape 2 : Sélection du preset
            <div className="tw-space-y-4">
              {isLoading ? (
                <div className="tw-flex tw-justify-center tw-items-center tw-py-12">
                  <div className="tw-animate-spin tw-h-8 tw-w-8 tw-border-4 tw-border-affinicar-primary tw-border-t-transparent tw-rounded-full"></div>
                </div>
              ) : presets && presets.length > 0 ? (
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                  {presets.map((preset, index) => {
                    const { Icon, color, bgColor } = presetIcons[index % presetIcons.length];

                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handlePresetSelect(preset)}
                        className="tw-flex tw-flex-col tw-items-start tw-p-5 tw-rounded-xl tw-border-2 tw-border-gray-300 tw-bg-white hover:tw-border-affinicar-light hover:tw-shadow-lg tw-transition-all tw-text-left"
                      >
                        <div className={`tw-p-2 tw-rounded-lg tw-mb-3 ${bgColor}`}>
                          <Icon className={`tw-w-6 tw-h-6 ${color}`} />
                        </div>
                        <h4 className="tw-text-base tw-font-bold tw-mb-2">{preset.label}</h4>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
                          {preset.description || 'Configuration recommandée'}
                        </p>
                        <div className="tw-space-y-1 tw-mb-3 tw-text-xs tw-text-gray-700">
                          <div>Durée : {preset.duration} mois</div>
                          <div>Plafond : {preset.financialCap.toLocaleString()} €</div>
                          <div>Prix : {preset.sellingPrice.toLocaleString()} €</div>
                        </div>
                        <span className="tw-px-3 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-bold tw-bg-gray-100 tw-text-gray-700 tw-mt-auto">
                          {preset.partsCount} pièces
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="tw-text-center tw-text-gray-500 tw-py-8">
                  Aucun preset disponible pour ce type de véhicule.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="tw-flex tw-justify-between tw-p-6 tw-border-t tw-gap-3">
          {step === 2 ? (
            <button
              type="button"
              onClick={handleBack}
              className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors"
            >
              Retour
            </button>
          ) : (
            <div />
          )}
          <button
            type="button"
            onClick={onSkip}
            data-testid="btn-skip-warranty-preset"
            className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors"
          >
            Continuer sans preset
          </button>
        </div>
      </div>
    </div>
  );
};
